<template>
  <Teleport to="body">
    <Transition name="lightbox-fade">
      <div
        v-if="open"
        class="fixed inset-0 z-[200] flex items-center justify-center bg-stone-950/90 p-4 backdrop-blur-sm md:p-8"
        role="dialog"
        aria-modal="true"
        :aria-label="labels.viewer"
        @click.self="emit('close')"
      >
        <button
          type="button"
          class="absolute right-4 top-4 grid h-11 w-11 place-items-center rounded-full border border-white/15 bg-white/10 text-white transition hover:bg-white/20 md:right-6 md:top-6"
          :aria-label="labels.close"
          @click="emit('close')"
        >
          <AppIcon name="x" class="h-5 w-5" />
        </button>

        <button
          v-if="items.length > 1"
          type="button"
          class="absolute left-3 top-1/2 grid h-11 w-11 -translate-y-1/2 place-items-center rounded-full border border-white/15 bg-white/10 text-white transition hover:bg-white/20 md:left-6 md:h-12 md:w-12"
          :aria-label="labels.previous"
          @click="emit('update:index', previousIndex)"
        >
          <AppIcon name="chevron-left" class="h-6 w-6" />
        </button>

        <button
          v-if="items.length > 1"
          type="button"
          class="absolute right-3 top-1/2 grid h-11 w-11 -translate-y-1/2 place-items-center rounded-full border border-white/15 bg-white/10 text-white transition hover:bg-white/20 md:right-6 md:h-12 md:w-12"
          :aria-label="labels.next"
          @click="emit('update:index', nextIndex)"
        >
          <AppIcon name="chevron-right" class="h-6 w-6" />
        </button>

        <figure class="flex max-h-full w-full max-w-5xl flex-col items-center">
          <img
            :src="currentItem.image"
            :alt="currentItem.imageAlt || currentItem.title"
            class="max-h-[calc(100vh-8rem)] w-auto max-w-full rounded-[1.25rem] object-contain shadow-float md:max-h-[calc(100vh-10rem)] md:rounded-[1.75rem]"
          />
          <figcaption class="mt-4 max-w-2xl text-center">
            <p class="text-lg font-semibold text-white md:text-xl">{{ currentItem.title }}</p>
            <p v-if="items.length > 1" class="mt-2 text-sm text-white/70">
              {{ String(activeIndex + 1).padStart(2, "0") }} / {{ String(items.length).padStart(2, "0") }}
            </p>
          </figcaption>
        </figure>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { computed, onBeforeUnmount, watch } from "vue";
import AppIcon from "./AppIcon.vue";

const props = defineProps({
  open: {
    type: Boolean,
    default: false
  },
  index: {
    type: Number,
    default: 0
  },
  items: {
    type: Array,
    default: () => []
  },
  labels: {
    type: Object,
    default: () => ({
      viewer: "Image viewer",
      close: "Close",
      previous: "Previous image",
      next: "Next image"
    })
  }
});

const emit = defineEmits(["close", "update:index"]);

const activeIndex = computed(() => {
  if (!props.items.length) return 0;
  return ((props.index % props.items.length) + props.items.length) % props.items.length;
});

const currentItem = computed(() => props.items[activeIndex.value] || { image: "", title: "", imageAlt: "" });

const previousIndex = computed(() => (activeIndex.value - 1 + props.items.length) % props.items.length);
const nextIndex = computed(() => (activeIndex.value + 1) % props.items.length);

const onKeydown = (event) => {
  if (!props.open) return;

  if (event.key === "Escape") {
    emit("close");
  } else if (event.key === "ArrowLeft" && props.items.length > 1) {
    emit("update:index", previousIndex.value);
  } else if (event.key === "ArrowRight" && props.items.length > 1) {
    emit("update:index", nextIndex.value);
  }
};

watch(
  () => props.open,
  (isOpen) => {
    document.body.style.overflow = isOpen ? "hidden" : "";
  }
);

watch(
  () => props.open,
  (isOpen) => {
    if (isOpen) {
      window.addEventListener("keydown", onKeydown);
    } else {
      window.removeEventListener("keydown", onKeydown);
    }
  },
  { immediate: true }
);

onBeforeUnmount(() => {
  window.removeEventListener("keydown", onKeydown);
  document.body.style.overflow = "";
});
</script>

<style scoped>
.lightbox-fade-enter-active,
.lightbox-fade-leave-active {
  transition: opacity 0.2s ease;
}

.lightbox-fade-enter-from,
.lightbox-fade-leave-to {
  opacity: 0;
}
</style>
